import {useCallback, useEffect, useRef, useState} from 'react';
import uniqid from 'uniqid';
import imgPlaceholder from '../assets/images/icon-placeholder.svg';
import {getConfigData} from '../middleware';
import {getPhotosSize, initPhotos} from './photos';
import {buildUploadName, uploadFile} from './photoUpload';

export const MAX_CONCURRENT_UPLOADS = 3;
const VALID_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

const pristineSlot = () => ({src: imgPlaceholder, pristine: true, size: 0});

const releaseSlot = (slot) => {
  if (slot && slot.isLocal && /^blob:/.test(slot.src || '')) {
    URL.revokeObjectURL(slot.src);
  }
};

/**
 * Owns the photo slots and the S3 upload queue for the AddPhotos steps.
 *
 * - Uploads run through a real pool (MAX_CONCURRENT_UPLOADS) and every upload
 *   is time-bounded, so one stalled request can't block the rest.
 * - Each file gets a unique object name, so same-named files don't collide.
 * - A slot only counts as a photo once its upload finished; failed uploads are
 *   rolled back to an empty slot.
 * - Files that don't fit (no free slot / size limit) are reported, not dropped.
 */
const usePhotoUploader = ({initialPhotos, photosKey}) => {
  const [photos, setPhotosState] = useState(
    () => initPhotos(initialPhotos).photos
  );
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');

  const photosRef = useRef(photos);
  const queueRef = useRef([]);
  const activeRef = useRef(0);
  const controllersRef = useRef({});
  const mountedRef = useRef(true);

  const updatePhotos = useCallback((updater) => {
    photosRef.current = updater(photosRef.current);
    if (mountedRef.current) setPhotosState(photosRef.current);
  }, []);

  const setProgress = useCallback((uploadId, status, progress) => {
    if (!mountedRef.current) return;
    setUploadProgress((prev) => ({
      ...prev,
      [uploadId]: {status, progress: progress !== undefined ? progress : 0},
    }));
  }, []);

  const clearProgress = useCallback((uploadId) => {
    if (!mountedRef.current) return;
    setUploadProgress((prev) => {
      const next = {...prev};
      delete next[uploadId];
      return next;
    });
  }, []);

  const appendError = useCallback((message) => {
    if (!mountedRef.current) return;
    setError((prev) => (prev ? `${prev} ${message}` : message));
  }, []);

  const runJob = useCallback(
    async ({file, uploadId}) => {
      const controller = new AbortController();
      controllersRef.current[uploadId] = controller;
      const uploadName = buildUploadName(file);

      try {
        const {s3Key} = await uploadFile(file, uploadName, photosKey, {
          signal: controller.signal,
          onStatus: (status) => setProgress(uploadId, status, 0),
          onProgress: (progress) => setProgress(uploadId, 'uploading', progress),
        });

        const slot = photosRef.current.find((p) => p.uploadId === uploadId);
        if (!slot) return; // removed while uploading

        const cdn = getConfigData()[getConfigData().env].cdn;
        const cdnUri = cdn.scheme + '://' + cdn.host.replace(/\/$/, '');
        releaseSlot(slot);
        updatePhotos((prev) =>
          prev.map((p) =>
            p.uploadId === uploadId
              ? {
                  ...p,
                  src: `${cdnUri}/uploads/${photosKey}/${uploadName}`,
                  s3Key,
                  isLocal: false,
                }
              : p
          )
        );
        setProgress(uploadId, 'complete', 100);
      } catch (e) {
        if (controller.signal.aborted) return; // removed or unmounted
        console.error(`Upload failed for file ${file.name}:`, e);
        const slot = photosRef.current.find((p) => p.uploadId === uploadId);
        if (slot) {
          releaseSlot(slot);
          updatePhotos((prev) =>
            prev.map((p) => (p.uploadId === uploadId ? pristineSlot() : p))
          );
        }
        clearProgress(uploadId);
        appendError(`Upload failed for ${file.name}. Please try again.`);
      } finally {
        delete controllersRef.current[uploadId];
      }
    },
    [photosKey, setProgress, clearProgress, appendError, updatePhotos]
  );

  const pump = useCallback(() => {
    while (
      activeRef.current < MAX_CONCURRENT_UPLOADS &&
      queueRef.current.length > 0
    ) {
      const job = queueRef.current.shift();
      activeRef.current++;
      runJob(job).finally(() => {
        activeRef.current--;
        pump();
      });
    }
    if (
      activeRef.current === 0 &&
      queueRef.current.length === 0 &&
      mountedRef.current
    ) {
      setIsUploading(false);
    }
  }, [runJob]);

  const handleFiles = useCallback(
    (fileList) => {
      const files = Array.from(fileList || []);
      if (files.length === 0) return;

      const maxSize = getConfigData().maxFileSizeForUpload;
      const messages = [];
      const notAdded = [];
      const jobs = [];
      const slots = [...photosRef.current];
      let size = getPhotosSize(slots);

      for (const file of files) {
        if (!VALID_TYPES.includes(file.type)) {
          messages.push(
            `Invalid file type: ${file.name}. Only JPEG and PNG files are allowed.`
          );
          continue;
        }
        const index = slots.findIndex((p) => p.pristine);
        if (index === -1) {
          notAdded.push(file.name);
          continue;
        }
        if (file.size + size > maxSize) {
          const mbsize = (file.size / 1024 / 1024).toFixed(2);
          const mbmax = maxSize / 1024 / 1024;
          messages.push(
            `Max size allowed (combined) is ${mbmax}MB. File '${file.name}' (${mbsize}MB) cannot be uploaded.`
          );
          continue;
        }

        size += file.size;
        const uploadId = uniqid();
        slots[index] = {
          src: URL.createObjectURL(file),
          pristine: false,
          isLocal: true,
          size: file.size,
          uploadId,
        };
        jobs.push({file, uploadId});
      }

      if (notAdded.length > 0) {
        messages.push(
          `Only ${photosRef.current.length} photos are allowed. Not added: ${notAdded.join(', ')}.`
        );
      }
      setError(messages.join(' '));

      if (jobs.length === 0) return;

      updatePhotos(() => slots);
      queueRef.current.push(...jobs);
      setIsUploading(true);
      pump();
    },
    [pump, updatePhotos]
  );

  const removePhoto = useCallback(
    (index) => {
      const slot = photosRef.current[index];
      if (!slot || slot.pristine) return;

      if (slot.uploadId) {
        queueRef.current = queueRef.current.filter(
          (job) => job.uploadId !== slot.uploadId
        );
        const controller = controllersRef.current[slot.uploadId];
        if (controller) controller.abort();
        clearProgress(slot.uploadId);
      }
      releaseSlot(slot);
      updatePhotos((prev) =>
        prev.map((p, i) => (i === index ? pristineSlot() : p))
      );
      pump();
    },
    [clearProgress, pump, updatePhotos]
  );

  const closeError = useCallback(() => setError(''), []);

  /** Photos whose upload finished, in the shape stored in the review. */
  const getUploadedPhotos = useCallback(
    () =>
      photosRef.current
        .filter((p) => !p.pristine && !p.isLocal)
        .map(({src, pristine, size, s3Key}) => ({src, pristine, size, s3Key})),
    []
  );

  useEffect(() => {
    mountedRef.current = true;
    const controllers = controllersRef.current;
    return () => {
      mountedRef.current = false;
      queueRef.current = [];
      Object.values(controllers).forEach((c) => c.abort());
    };
  }, []);

  return {
    photos,
    uploadProgress,
    isUploading,
    error,
    showError: error !== '',
    closeError,
    handleFiles,
    removePhoto,
    getUploadedPhotos,
  };
};

export default usePhotoUploader;
